/**
 * Post-generation "save this portrait to your account" prompt.
 *
 * Anonymous: small inline signup form (email + password) — on submit we
 * sign up and then call POST /api/auth/claim-job to bind the freshly
 * generated style_job to the new user.
 *
 * Authenticated: shows a confirmation strip + a link to /account. We also
 * fire a one-shot claim-job in case the user was logged in mid-flow on a
 * job that started anonymously.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, BookmarkPlus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import PasswordInput from "./PasswordInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SavePortraitCTA = ({ jobId, petName }) => {
  const { user, signup } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const claimAttemptedRef = useRef(false);

  // If we already have an authenticated user, fire-and-forget the claim
  // exactly once per jobId so the portrait is bound to the account even
  // when the user logged in mid-flow.
  useEffect(() => {
    if (!user || !jobId || claimAttemptedRef.current) return;
    claimAttemptedRef.current = true;
    axios
      .post(`${API}/auth/claim-job`, { job_id: jobId })
      .then(() => setClaimed(true))
      .catch(() => {
        // Silent — most common reason is the job is already bound to
        // another account, which the user can't (and shouldn't) override.
      });
  }, [user, jobId]);

  if (!jobId) return null;

  if (user) {
    return (
      <div
        className="mt-8 mx-auto max-w-xl bg-[#F8F3EA] border border-[#EAE5DF] px-5 py-4 flex items-center justify-between gap-3"
        data-testid="save-portrait-authed"
      >
        <div className="flex items-center gap-3">
          <span className="bg-[#C8A27A] text-white rounded-full p-1.5">
            <Check className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm text-[#1A1A1A] font-medium">
              {claimed ? "Saved to your account" : "Linked to your account"}
            </p>
            <p className="text-xs text-[#6B6B6B]">
              {petName ? `${petName}'s portrait` : "This portrait"} is now in
              your gallery.
            </p>
          </div>
        </div>
        <Link
          to="/account"
          className="text-sm text-[#9B6A2D] underline whitespace-nowrap"
          data-testid="save-portrait-view-account"
        >
          View account
        </Link>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const idt = identifier.trim();
    if (!idt) {
      toast.error("Enter your email or phone");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await signup({ identifier: idt, password, name: null });
      // signup() already sets the axios Authorization header.
      try {
        await axios.post(`${API}/auth/claim-job`, { job_id: jobId });
        setClaimed(true);
        toast.success("Portrait saved to your account");
      } catch (claimErr) {
        // Signup succeeded but claim failed (e.g. job already bound). Don't
        // block the user — they're logged in either way.
        toast.success("Account created");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mt-8 mx-auto max-w-xl bg-[#F8F3EA] border border-[#EAE5DF] p-5"
      data-testid="save-portrait-anon"
    >
      <div className="flex items-start gap-3 mb-3">
        <span className="bg-[#1A1A1A] text-white rounded-full p-2 flex-shrink-0">
          <BookmarkPlus className="h-4 w-4" />
        </span>
        <div>
          <p className="text-sm font-semibold text-[#1A1A1A]">
            Save this portrait to your account
          </p>
          <p className="text-xs text-[#6B6B6B] mt-0.5">
            Sign up free — keep your AI portraits, reorder anytime, and skip the
            upload next time.
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-2" data-testid="save-portrait-form">
        <input
          type="text"
          placeholder="Email or phone number"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          className="w-full border border-[#EAE5DF] px-3 py-2.5 text-sm bg-white text-[#1A1A1A]"
          autoComplete="username"
          required
          data-testid="save-portrait-identifier"
        />
        <div className="flex gap-2">
          <input
            type="password"
            placeholder="Create password (6+ characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="flex-1 min-w-0 border border-[#EAE5DF] px-3 py-2.5 text-sm bg-white text-[#1A1A1A]"
            autoComplete="new-password"
            required
            data-testid="save-portrait-password"
          />
          <button
            type="submit"
            disabled={submitting}
            className="btn-accent text-sm px-4 disabled:opacity-60"
            data-testid="save-portrait-submit"
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
      <p className="text-xs text-[#9A9A9A] text-center mt-3">
        Already have an account?{" "}
        <Link
          to={`/login?next=${encodeURIComponent(`/styles?job_id=${jobId}`)}`}
          className="underline"
          data-testid="save-portrait-login-link"
        >
          Log in
        </Link>
      </p>
    </div>
  );
};

export default SavePortraitCTA;
